import React, { useState, useEffect, useCallback } from 'react';
import { StandardButton } from '../standard';
import {
  GripVertical, Eye, EyeOff, ChevronUp, ChevronDown,
  Home, Users, UserPlus, DollarSign, Contact, Headphones,
  CheckSquare, Calendar, BarChart3, Settings, RotateCcw, Save,
  FolderOpen, Info, Package, BookOpen, FileText, Receipt
} from 'lucide-react';

// ============================================
// INTERFACES
// ============================================

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  visible: boolean;
  order: number;
  path: string;
  group: string;
}

interface MenuGroup {
  id: string;
  label: string;
  visible: boolean;
  order: number;
  items: string[]; // MenuItem IDs
}

interface NavigationConfig {
  menuItems: MenuItem[];
  menuGroups: MenuGroup[];
  version: string;
  lastModified: string;
}

// ============================================
// CONSTANTS
// ============================================

const STORAGE_KEY = 'navigation_config';

const DEFAULT_MENU_ITEMS: MenuItem[] = [
  { id: 'home', label: 'Home', icon: 'Home', visible: true, order: 1, path: '/home', group: 'standalone' },
  { id: 'leads', label: 'Leads', icon: 'UserPlus', visible: true, order: 2, path: '/leads', group: 'sales' },
  { id: 'clients', label: 'Clients', icon: 'Users', visible: true, order: 3, path: '/clients', group: 'sales' },
  { id: 'opportunities', label: 'Opportunities', icon: 'DollarSign', visible: true, order: 4, path: '/opportunities', group: 'sales' },
  { id: 'products', label: 'Products', icon: 'Package', visible: true, order: 5, path: '/products', group: 'sales' },
  { id: 'pricebooks', label: 'Pricebooks', icon: 'BookOpen', visible: true, order: 6, path: '/pricebooks', group: 'sales' },
  { id: 'invoices', label: 'Invoices', icon: 'FileText', visible: true, order: 7, path: '/invoices', group: 'sales' },
  { id: 'transactions', label: 'Transactions', icon: 'Receipt', visible: true, order: 8, path: '/transactions', group: 'sales' },
  { id: 'contacts', label: 'Contacts', icon: 'Contact', visible: true, order: 9, path: '/contacts', group: 'service' },
  { id: 'cases', label: 'Cases', icon: 'Headphones', visible: true, order: 10, path: '/cases', group: 'service' },
  { id: 'tasks', label: 'Tasks', icon: 'CheckSquare', visible: true, order: 11, path: '/tasks', group: 'productivity' },
  { id: 'events', label: 'Events', icon: 'Calendar', visible: true, order: 12, path: '/events', group: 'productivity' },
  { id: 'reports', label: 'Reports', icon: 'BarChart3', visible: true, order: 13, path: '/reports', group: 'analytics' },
];

const DEFAULT_MENU_GROUPS: MenuGroup[] = [
  { id: 'standalone', label: 'Standalone', visible: true, order: 1, items: ['home'] },
  { id: 'sales', label: 'Sales', visible: true, order: 2, items: ['leads', 'clients', 'opportunities', 'products', 'pricebooks', 'invoices', 'transactions'] },
  { id: 'service', label: 'Service', visible: true, order: 3, items: ['contacts', 'cases'] },
  { id: 'productivity', label: 'Productivity', visible: true, order: 4, items: ['tasks', 'events'] },
  { id: 'analytics', label: 'Analytics', visible: true, order: 5, items: ['reports'] },
];

// ============================================
// ICON MAP
// ============================================

const IconMap: Record<string, React.FC<{ className?: string }>> = {
  Home,
  Users,
  UserPlus,
  DollarSign,
  Contact,
  Headphones,
  CheckSquare,
  Calendar,
  BarChart3,
  Settings,
  FolderOpen,
  Package,
  BookOpen,
  FileText,
  Receipt,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const loadNavigationConfig = (): NavigationConfig => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error loading navigation config:', e);
  }
  return {
    menuItems: DEFAULT_MENU_ITEMS,
    menuGroups: DEFAULT_MENU_GROUPS,
    version: '1.0.0',
    lastModified: new Date().toISOString(),
  };
};

const saveNavigationConfig = (config: NavigationConfig): void => {
  try {
    config.lastModified = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    // Dispatch event for GlobalNavigation to pick up changes
    window.dispatchEvent(new CustomEvent('navigation-config-updated', { detail: config }));
  } catch (e) {
    console.error('Error saving navigation config:', e);
  }
};

// ============================================
// MAIN COMPONENT
// ============================================

const NavigationConfigManager: React.FC = () => {
  const [config, setConfig] = useState<NavigationConfig>(loadNavigationConfig);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['sales', 'service', 'productivity']));

  // Load config on mount
  useEffect(() => {
    setConfig(loadNavigationConfig());
  }, []);

  // Toggle item visibility
  const handleToggleItemVisibility = useCallback((itemId: string) => {
    setConfig(prev => ({
      ...prev,
      menuItems: prev.menuItems.map(item =>
        item.id === itemId ? { ...item, visible: !item.visible } : item
      ),
    }));
    setHasChanges(true);
  }, []);

  // Toggle group visibility
  const handleToggleGroupVisibility = useCallback((groupId: string) => {
    setConfig(prev => ({
      ...prev,
      menuGroups: prev.menuGroups.map(group =>
        group.id === groupId ? { ...group, visible: !group.visible } : group
      ),
    }));
    setHasChanges(true);
  }, []);

  // Move item within group
  const handleMoveItem = useCallback((itemId: string, direction: 'up' | 'down') => {
    setConfig(prev => {
      const item = prev.menuItems.find(i => i.id === itemId);
      if (!item) return prev;

      const groupItems = prev.menuItems
        .filter(i => i.group === item.group)
        .sort((a, b) => a.order - b.order);

      const currentIndex = groupItems.findIndex(i => i.id === itemId);
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      if (newIndex < 0 || newIndex >= groupItems.length) return prev;

      // Swap orders
      const otherItem = groupItems[newIndex];
      const tempOrder = item.order;

      return {
        ...prev,
        menuItems: prev.menuItems.map(i => {
          if (i.id === itemId) return { ...i, order: otherItem.order };
          if (i.id === otherItem.id) return { ...i, order: tempOrder };
          return i;
        }),
      };
    });
    setHasChanges(true);
  }, []);

  // Move group
  const handleMoveGroup = useCallback((groupId: string, direction: 'up' | 'down') => {
    setConfig(prev => {
      const groups = [...prev.menuGroups].sort((a, b) => a.order - b.order);
      const currentIndex = groups.findIndex(g => g.id === groupId);
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      if (newIndex < 0 || newIndex >= groups.length) return prev;

      // Swap orders
      const group = groups[currentIndex];
      const otherGroup = groups[newIndex];
      const tempOrder = group.order;

      return {
        ...prev,
        menuGroups: prev.menuGroups.map(g => {
          if (g.id === groupId) return { ...g, order: otherGroup.order };
          if (g.id === otherGroup.id) return { ...g, order: tempOrder };
          return g;
        }),
      };
    });
    setHasChanges(true);
  }, []);

  // Toggle group expansion
  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Save changes
  const handleSave = () => {
    saveNavigationConfig(config);
    setHasChanges(false);
    setSaveMessage('Navigation configuration saved successfully!');
    setTimeout(() => setSaveMessage(null), 3000);
  };

  // Reset to defaults
  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset navigation to default settings? This cannot be undone.')) {
      const defaultConfig: NavigationConfig = {
        menuItems: DEFAULT_MENU_ITEMS,
        menuGroups: DEFAULT_MENU_GROUPS,
        version: '1.0.0',
        lastModified: new Date().toISOString(),
      };
      setConfig(defaultConfig);
      saveNavigationConfig(defaultConfig);
      setHasChanges(false);
      setSaveMessage('Navigation reset to defaults!');
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  // Get items for a group
  const getGroupItems = (groupId: string) => {
    return config.menuItems
      .filter(item => item.group === groupId)
      .sort((a, b) => a.order - b.order);
  };

  // Get sorted groups
  const sortedGroups = [...config.menuGroups].sort((a, b) => a.order - b.order);

  // Get visible items count
  const visibleItemsCount = config.menuItems.filter(item => item.visible).length;
  const totalItemsCount = config.menuItems.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Navigation Configuration
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Customize the main navigation menu items, their order, and visibility
          </p>
        </div>
        <div className="flex gap-2">
          <StandardButton
            variant="secondary"
            onClick={handleReset}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Default
          </StandardButton>
          <StandardButton
            variant="primary"
            onClick={handleSave}
            disabled={!hasChanges}
          >
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </StandardButton>
        </div>
      </div>

      {/* Status Messages */}
      {saveMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-sm text-green-800 dark:text-green-200">{saveMessage}</p>
        </div>
      )}

      {hasChanges && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            You have unsaved changes. Click "Save Changes" to apply them.
          </p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Menu Items</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalItemsCount}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">Visible Items</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{visibleItemsCount}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">Menu Groups</p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{config.menuGroups.length}</p>
        </div>
      </div>

      {/* Menu Groups */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Menu Groups & Items</h4>

        {sortedGroups.map((group, groupIndex) => {
          const groupItems = getGroupItems(group.id);
          const isExpanded = expandedGroups.has(group.id);
          const visibleGroupItems = groupItems.filter(i => i.visible).length;

          return (
            <div
              key={group.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Group Header */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50">
                <div className="flex items-center gap-3">
                  <GripVertical className="h-5 w-5 text-gray-400 cursor-move" />
                  <button
                    onClick={() => toggleGroupExpansion(group.id)}
                    className="flex items-center gap-2"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronUp className="h-4 w-4 text-gray-500" />
                    )}
                    <span className="font-medium text-gray-900 dark:text-white">{group.label}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      ({visibleGroupItems}/{groupItems.length} visible)
                    </span>
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleGroupVisibility(group.id)}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    title={group.visible ? 'Hide group' : 'Show group'}
                  >
                    {group.visible ? (
                      <Eye className="h-4 w-4 text-green-600 dark:text-green-400" />
                    ) : (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                  <button
                    onClick={() => handleMoveGroup(group.id, 'up')}
                    disabled={groupIndex === 0}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move group up"
                  >
                    <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  </button>
                  <button
                    onClick={() => handleMoveGroup(group.id, 'down')}
                    disabled={groupIndex === sortedGroups.length - 1}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move group down"
                  >
                    <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Group Items */}
              {isExpanded && (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {groupItems.map((item, itemIndex) => {
                    const IconComponent = IconMap[item.icon] || FolderOpen;
                    return (
                      <div
                        key={item.id}
                        className={`flex items-center justify-between p-3 pl-12 ${
                          !item.visible ? 'bg-gray-50 dark:bg-gray-900/30' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <GripVertical className="h-4 w-4 text-gray-300 cursor-move" />
                          <div className={`p-1.5 rounded ${item.visible ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                            <IconComponent className={`h-4 w-4 ${item.visible ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <span className={`font-medium ${item.visible ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
                              {item.label}
                            </span>
                            <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">{item.path}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleItemVisibility(item.id)}
                            className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            title={item.visible ? 'Hide item' : 'Show item'}
                          >
                            {item.visible ? (
                              <Eye className="h-4 w-4 text-green-600 dark:text-green-400" />
                            ) : (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                          <button
                            onClick={() => handleMoveItem(item.id, 'up')}
                            disabled={itemIndex === 0}
                            className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move item up"
                          >
                            <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                          </button>
                          <button
                            onClick={() => handleMoveItem(item.id, 'down')}
                            disabled={itemIndex === groupItems.length - 1}
                            className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move item down"
                          >
                            <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Preview Section */}
      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Eye className="h-4 w-4" />
          Navigation Preview
        </h4>
        <div className="flex flex-wrap gap-2">
          {sortedGroups
            .filter(group => group.visible)
            .map(group => {
              const visibleItems = getGroupItems(group.id).filter(item => item.visible);
              if (visibleItems.length === 0) return null;

              return (
                <div key={group.id} className="flex items-center gap-1">
                  {group.id !== 'standalone' && (
                    <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm font-medium">
                      {group.label}
                    </span>
                  )}
                  {visibleItems.map(item => {
                    const IconComponent = IconMap[item.icon] || FolderOpen;
                    return (
                      <div
                        key={item.id}
                        className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-sm text-gray-900 dark:text-white flex items-center gap-2"
                      >
                        <IconComponent className="h-3.5 w-3.5 text-gray-500" />
                        {item.label}
                      </div>
                    );
                  })}
                  {group.id !== 'standalone' && <div className="w-2" />}
                </div>
              );
            })}
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2 flex items-center gap-2">
          <Info className="h-4 w-4" />
          How to Use
        </h4>
        <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
          <li>• Click the eye icon to show/hide menu items or groups</li>
          <li>• Use ↑↓ buttons to reorder items within groups or groups themselves</li>
          <li>• Click group headers to expand/collapse item lists</li>
          <li>• Changes are saved to localStorage and persist across sessions</li>
          <li>• Hidden items won't appear in the main navigation</li>
          <li>• Click "Reset to Default" to restore original settings</li>
        </ul>
      </div>

      {/* Last Modified */}
      {config.lastModified && (
        <p className="text-xs text-gray-500 dark:text-gray-400 text-right">
          Last modified: {new Date(config.lastModified).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default NavigationConfigManager;
